import { ApiError } from "./errors";
import { type BuildHeadersInput, buildHeaders } from "./headers";

/** localStorage key for the JWT. Must mirror apps/web/lib/auth-token-key.ts. */
const DEFAULT_TOKEN_KEY = "kupikupi-token";

/**
 * Default getter installed synchronously at module load — no setup step needed.
 * Any apiFetch() in the browser can read the JWT on its very first call,
 * BEFORE any React effect has run. This fixes the long-standing race where
 * the previous useEffect-based registration left the first frame of fetches
 * anonymous, which (combined with TanStack Query's queryKey cache) cached the
 * anonymous response under the same key as the authenticated render — leaving
 * isFavorite/owned/etc. permanently false until a hard refetch.
 *
 * On the server (SSR / prefetchQuery) localStorage is absent, so it returns
 * null and the request is genuinely anonymous. Pair with `authPresent` in
 * the query key (see useProductDetail) so the client doesn't reuse the SSR
 * anonymous cache once a token is available.
 *
 * Tests + alt-storage apps can still override via `setAuthTokenGetter`.
 */
function defaultAuthTokenGetter(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(DEFAULT_TOKEN_KEY);
  } catch {
    return null;
  }
}

let _getAuthToken: () => string | null = defaultAuthTokenGetter;

/** Override the JWT getter (e.g. tests, alternative storage keys). */
export function setAuthTokenGetter(fn: () => string | null) {
  _getAuthToken = fn;
}

export interface ApiFetchContext {
  baseUrl: string;
}

export interface ApiFetchInit extends BuildHeadersInput {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  next?: { revalidate?: number | false; tags?: string[] };
  cache?: RequestCache;
  /**
   * Allow a successful (`code:200`/`success:true`) envelope to carry a null /
   * absent `result`. Write-only endpoints like `/shopcart/edit/service` return
   * a bare `Result.OK()` (no `setResult`), so the default null-data guard would
   * wrongly throw `BAD_ENVELOPE` and block the mutation's `onSuccess`
   * (pre-order refetch) — the exact #261 video-fee-not-updating bug. Set this
   * for endpoints whose success is signalled by the envelope alone.
   */
  allowEmptyData?: boolean;
}

interface BackendEnvelope<T> {
  /** Jeecg returns numeric code (200 = OK, 500 = error). Older endpoints may send string. */
  code: number | string;
  /** Some endpoints also set this; treat falsy as failure when present. */
  success?: boolean;
  message?: string;
  data?: T;
  result?: T;
}

export async function apiFetch<T = unknown>(
  ctx: ApiFetchContext,
  path: string,
  init: ApiFetchInit = {},
): Promise<T> {
  // Auto-attach the JWT read from localStorage unless the caller already supplied
  // an explicit authToken (e.g. SSR prefetch with a cookie-extracted token).
  if (init.authToken === undefined) {
    const t = _getAuthToken();
    if (t) init = { ...init, authToken: t };
  }

  const url = `${ctx.baseUrl}${path}`;
  const headers = buildHeaders(init);
  // FormData (multipart file upload, e.g. /sys/common/upload) must NOT be
  // JSON-stringified, and the browser/runtime needs to set the multipart
  // boundary itself — so drop our default `application/json` Content-Type and
  // pass the FormData through untouched.
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  if (isFormData && headers && typeof headers === "object" && !Array.isArray(headers)) {
    delete (headers as Record<string, string>)["Content-Type"];
  }
  const res = await fetch(url, {
    method: init.method ?? "GET",
    headers,
    cache: init.cache ?? "no-store",
    ...(init.body !== undefined
      ? { body: isFormData ? (init.body as FormData) : JSON.stringify(init.body) }
      : {}),
    ...(init.next ? { next: init.next } : {}),
  });

  const contentType = res.headers.get("Content-Type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: BackendEnvelope<T> | { message?: string } = isJson ? await res.json() : {};

  if (!res.ok) {
    throw new ApiError({
      code: String((payload as BackendEnvelope<T>).code ?? `HTTP_${res.status}`),
      message: (payload as BackendEnvelope<T>).message ?? res.statusText,
      httpStatus: res.status,
      details: payload,
    });
  }

  const envelope = payload as BackendEnvelope<T>;

  // Jeecg's Result.error() returns HTTP 200 with envelope { success:false, code:500 } —
  // surface as a real error so useQuery.isError flips on, instead of silently
  // returning a null payload that consumers treat as "empty results".
  const envelopeOk =
    (envelope.success === undefined || envelope.success === true) &&
    (envelope.code === undefined ||
      envelope.code === 200 ||
      envelope.code === "200" ||
      envelope.code === "0");
  if (!envelopeOk) {
    throw new ApiError({
      code: String(envelope.code ?? "ENVELOPE_ERROR"),
      message: envelope.message ?? "Backend returned a non-success envelope",
      httpStatus: res.status,
      details: payload,
    });
  }

  const data = envelope.data ?? envelope.result;
  if (data === undefined || data === null) {
    // Write-only endpoints (e.g. /shopcart/edit/service) legitimately return a
    // success envelope with no payload — let callers opt into that instead of
    // throwing BAD_ENVELOPE and silently dropping the mutation success (#261).
    if (init.allowEmptyData) {
      return undefined as T;
    }
    throw new ApiError({
      code: "BAD_ENVELOPE",
      message: "Backend response missing data field",
      httpStatus: res.status,
      details: payload,
    });
  }
  return data;
}
